'use client'

import { InView } from 'react-intersection-observer'
import { useCallback, useEffect, useState, useRef, ReactNode } from 'react'
import {
  Image,
  Carousel,
  Dialog,
  Icon,
  Button,
  Divider,
  Navigation,
  Container,
} from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { userViewItemGallery } from '@marketplace-web/item-page/item-page-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import { ItemPageGalleryPluginModel } from '../types'
import FavouriteButton from '../FavouriteButton'

type Props = {
  data: ItemPageGalleryPluginModel
  favouriteButton?: ReactNode
}

const MobileImageCarousel = ({ data, favouriteButton }: Props) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [mainIndex, setMainIndex] = useState(0)
  const [dialogIndex, setDialogIndex] = useState(0)
  const { track } = useTracking()
  const translate = useTranslate('item.gallery.a11y')
  const touchStartRef = useRef<{ clientX: number; clientY: number } | null>(null)

  const handleImageClick = useCallback(() => {
    setDialogIndex(mainIndex)
    // without this, the dialogIndex gets set after the dialog renders,
    // which results in the default value (0) being used on first render
    setTimeout(() => setIsDialogOpen(true), 0)
  }, [mainIndex])

  const handleDialogClose = useCallback(() => {
    setIsDialogOpen(false)
  }, [])

  useEffect(() => {
    if (!isDialogOpen) {
      setMainIndex(dialogIndex)
    }
  }, [isDialogOpen, dialogIndex])

  const handleImageView =
    (index: number, imageView: 'item_page' | 'fullscreen') => (inView: boolean) => {
      if (!inView) return

      track(
        userViewItemGallery({
          itemId: data.id,
          imagePosition: index + 1,
          isLoop: true,
          imageCount: data.photos.length,
          contentType: 'gallery_image',
          galleryContainsAd: false,
          imageView,
        }),
      )
    }

  // Touch event handlers to allow vertical scrolling
  const handleTouchStart = (event: React.TouchEvent) => {
    const touch = event.touches[0]
    if (touch) {
      touchStartRef.current = { clientX: touch.clientX, clientY: touch.clientY }
    }
  }

  const handleTouchMove = (event: React.TouchEvent) => {
    if (!touchStartRef.current) return
    const touch = event.touches[0]
    if (!touch) return
    const deltaX = Math.abs(touch.clientX - touchStartRef.current.clientX)
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.clientY)
    // If vertical movement is greater than horizontal, allow default scrolling behavior
    if (deltaY > deltaX) {
      event.stopPropagation()
    }
  }

  const handleTouchEnd = () => {
    touchStartRef.current = null
  }

  return (
    <>
      {favouriteButton || (
        <FavouriteButton
          isFavourited={data.isFavourite}
          itemId={data.id}
          initialFavouriteCount={data.favouriteCount}
          itemUserId={data.ownerId}
        />
      )}
      <div
        className="mobile-image-carousel"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <Carousel
          arrows={data.photos.length > 1 ? 'inside' : 'hidden'}
          navigationWithKeyboard
          isInfinite
          slides={data.photos.map((photo, index) => (
            <button
              key={photo.id}
              type="button"
              onClick={handleImageClick}
              className="mobile-image-carousel__image-button"
              data-testid={`mobile-image-carousel__image-button-${index}`}
            >
              <InView
                className="mobile-image-carousel__image"
                as="div"
                onChange={handleImageView(index, 'item_page')}
                triggerOnce
              >
                <Image
                  src={photo.url}
                  alt={data.photos.length === 1 ? `${data.title}` : `${data.title} ${index + 1}`}
                  scaling="cover"
                />
              </InView>
            </button>
          ))}
          onSlideInteract={setMainIndex}
          index={mainIndex}
          hideNavigation={data.photos.length === 1}
        />
      </div>
      <Dialog
        show={isDialogOpen}
        defaultCallback={handleDialogClose}
        testId="mobile-photo-gallery-modal"
        contentDimensions={{
          width: '100%',
          height: '100%',
        }}
        className="mobile-photo-gallery-modal"
        isModal={false}
      >
        <Navigation
          body={`${dialogIndex + 1} / ${data.photos.length}`}
          right={
            <Button
              icon={<Icon name={X24} title="Close" color={Icon.Color.GreyscaleLevel1} />}
              inline
              styling={Button.Styling.Flat}
              onClick={handleDialogClose}
              aria-label={translate('modal.actions.close')}
            />
          }
          theme="transparent"
        />
        <Divider />
        <Container>
          <Carousel
            arrows={data.photos.length > 1 ? 'inside' : 'hidden'}
            navigationWithKeyboard
            isInfinite
            slides={data.photos.map((photo, index) => (
              <InView
                key={photo.id}
                className="mobile-photo-gallery-modal__image"
                as="div"
                onChange={handleImageView(index, 'fullscreen')}
                triggerOnce
              >
                <Image
                  src={photo.url}
                  alt={data.photos.length === 1 ? `${data.title}` : `${data.title} ${index + 1}`}
                  scaling="contain"
                />
              </InView>
            ))}
            styling="floating"
            onSlideInteract={setDialogIndex}
            hideNavigation
            index={dialogIndex}
            data-testid="mobile-photo-gallery-modal__carousel"
          />
        </Container>
      </Dialog>
    </>
  )
}

export default MobileImageCarousel
